
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { flatMap as _flatmap } from 'lodash';
import { TemplatesPriorityService } from '@/app/services/template-priority.service';
import { catchError, of, from, timer, concatMap, toArray, delayWhen, Subscription, forkJoin } from 'rxjs';
import { factory } from '@/app/shared/app-logging';
import { ExportService } from '@/app/services/export.service';
import { CustomPriorityByEventType, SaveTemplatePriorityRequest, TemplatePriorityExportOppItem, TemplatePriorityExportRequest, TemplatePriorityModel } from '@/app/model/template.priority.model';
import { DropdownEntry } from '@/app/model/dropdown-entry';
import { Product } from '@/app/model/product';

const logger = factory.getLogger('TemplatePriorityComponent');

@Component({
  selector: "app-template-priority",
  templateUrl: "./template-priority.component.html",
  styleUrls: ["./template-priority.component.scss"],
})
export class TemplatePriorityComponent implements OnInit, OnDestroy {

  private subscriptions$: Subscription[] = [];

  //create element icon
  public iconInfo: any = document.createElement(
    'dsy-icon'
  );

  public getScreenWidth: any;
  public getScreenHeight: any;
  public lengthByScreen: any = 4;
  public editEnabled: boolean = false;

  public openExportModal: boolean = false;

  private opps: Array<Product> = [];
  private dropdownSelectedEntries: Array<DropdownEntry> = [];
  private defaultTemplatePriorities: Array<TemplatePriorityModel> = [];
  private templatePriorities: Array<TemplatePriorityModel> = [];
  private editedTemplatePriorities: Array<TemplatePriorityModel> = [];

  //catch err;
  error: any;

  public openSaveModal: boolean = false;
  public saveModalMessage: string;
  public exportModalMessage: string = 'This action will allow to export into a CSV file the template priorities configured for the products selected. Proceed?';

  constructor(
    private exportService: ExportService,
    public templatePriorityService: TemplatesPriorityService
  ) {

  }

  @HostListener("window:resize", ["$event"])
  onWindowResize() {
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    if (window.innerWidth < 1600 && window.innerWidth > 500) {
      this.lengthByScreen = 3;
    } else if (window.innerWidth < 500) {
      this.lengthByScreen = 0;
    } else {
      this.lengthByScreen = 4;
    }
  }

  ngOnInit(): void {

    this.onWindowResize();

    // subscribe to template priority state changes
    this.subscriptions$.push(this.templatePriorityService.getAction().subscribe((value) => {
      if (value === 'EDIT') {
        this.editEnabled = true;
      }
      if (value === 'CANCEL') {
        this.editEnabled = false;
        this.templatePriorityService.setIsSaveEnabled(false);
      }
      if (value === 'SAVE') {
        if (this.dropdownSelectedEntries.length == 0 || (this.dropdownSelectedEntries.length == 1 && this.dropdownSelectedEntries[0].product.id == null)) {
          this.saveModalMessage = 'Any changes done to the Default product will apply to all templates that have not been modified. Proceed?';
        } else if (this.dropdownSelectedEntries.length == 0 || (this.dropdownSelectedEntries.length == 1 && this.dropdownSelectedEntries[0].product.id == 'ALL')) {
          this.saveModalMessage = 'Changes will be applied to all Operator, Programs and Products. Proceed?';
        } else {
          this.saveModalMessage = 'Do you want to confirm the changes?';
        }
        this.openSaveModal = true;
      }
    }));
    // subscribe to template priority OPPs
    this.subscriptions$.push(this.templatePriorityService.getOpps().subscribe((value) => {
      this.opps = value;
    }));
    // subscribe to template priority selected OPPs changes
    this.subscriptions$.push(this.templatePriorityService.getDropdownSelectedEntries().subscribe((value) => {
      this.dropdownSelectedEntries = value;
    }));
    // subscribe to template default priority list
    this.subscriptions$.push(this.templatePriorityService.getDefaultTemplatePriorities().subscribe((value) => {
      this.defaultTemplatePriorities = value;
    }));
    // subscribe to template priority list
    this.subscriptions$.push(this.templatePriorityService.getTemplatePriorities().subscribe((value) => {
      this.templatePriorities = value;
    }));
    // subscribe to template priority form changes
    this.subscriptions$.push(this.templatePriorityService.getEditedTemplatePriorities().subscribe((value) => {
      this.editedTemplatePriorities = value;
    }));
    // subscribe to template priority errors
    this.subscriptions$.push(this.templatePriorityService.getError().subscribe((value: string) => {
      this.error = value;
    }));
    // Subscribe to Export Modal
    this.subscriptions$.push(this.exportService.getOpenExportModal().subscribe(value => {
      this.openExportModal = value;
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }

  private _saveDefaultTemplatePriorities() {
    this.templatePriorityService.setIsLoadingActive(true);
    // Find differences
    const templatePrioritiesToUpdate: Array<TemplatePriorityModel> = [];
    this.editedTemplatePriorities.forEach((editedTemplatePriority: TemplatePriorityModel) => {
      const defaultTemplatePriority = this.templatePriorities.find((tp) => tp.template_name == editedTemplatePriority.template_name);
      if (JSON.stringify(defaultTemplatePriority) !== JSON.stringify(editedTemplatePriority)) {
        templatePrioritiesToUpdate.push(editedTemplatePriority);
      }
    });

    const templatePriorityRequests: Array<SaveTemplatePriorityRequest> = [];
    templatePrioritiesToUpdate.forEach((templatePriorityToUpdate: TemplatePriorityModel) => {
      if (!templatePriorityToUpdate.customizations || templatePriorityToUpdate.customizations.length == 0) {
        // Update with no custom priorities by event type
        templatePriorityToUpdate.event_types.forEach((eventType: string) => {
          templatePriorityRequests.push(
            {
              template_name: templatePriorityToUpdate.template_name,
              event_type: eventType,
              priority: templatePriorityToUpdate.priority,
              product_id: null
            }
          );
        });
      } else {
        // Update with custom priorities by event type
        templatePriorityToUpdate.customizations.forEach((customPriority: CustomPriorityByEventType) => {
          templatePriorityRequests.push(
            {
              template_name: templatePriorityToUpdate.template_name,
              event_type: customPriority.event_type,
              priority: customPriority.priority,
              product_id: null
            }
          );
        });
      }
    });

    this._saveTemplatePriorities(templatePriorityRequests);
  }

  private _saveCustomTemplatePriorities(allOpps: boolean = false) {
    this.templatePriorityService.setIsLoadingActive(true);
    const templatePrioritiesToUpdate: Array<TemplatePriorityModel> = [];
    const templatePrioritiesToUpdateForSingleOpp: Array<TemplatePriorityModel> = [];
    // Find differences 
    this.editedTemplatePriorities.forEach((editedTemplatePriority: TemplatePriorityModel) => {
      const defaultTemplatePriority = this.templatePriorities.find((tp) => tp.template_name == editedTemplatePriority.template_name);
      if (JSON.stringify(editedTemplatePriority) !== JSON.stringify(defaultTemplatePriority)) {
        templatePrioritiesToUpdate.push(editedTemplatePriority);
      }
    });

    const dropdownEntries: DropdownEntry[] = allOpps ? this.opps.map((product: Product) => { return {
        labelContent: '',
        isSelected: true,
        hasBorderAbove: false,
        hasBorderBelow: false,
        product: product
      }}) : this.dropdownSelectedEntries;

    const templatePriorityRequests: Array<SaveTemplatePriorityRequest> = [];
    templatePrioritiesToUpdate.forEach((templatePriorityToUpdate: TemplatePriorityModel) => {
      if (!templatePriorityToUpdate.customizations || templatePriorityToUpdate.customizations.length == 0) {
        // Update with no custom priorities by event type for each selected product
        dropdownEntries.forEach((dropdownEntry: DropdownEntry) => {
          templatePriorityToUpdate.event_types.forEach((eventType: string) => {
            templatePriorityRequests.push(
              {
                template_name: templatePriorityToUpdate.template_name,
                event_type: eventType,
                priority: templatePriorityToUpdate.priority,
                product_id: dropdownEntry.product.id
              }
            );
          });
        });
      } else {
        // Update with custom priorities by event type for each selected product
        templatePriorityToUpdate.customizations.forEach((customPriority: CustomPriorityByEventType) => {
          dropdownEntries.forEach((dropdownEntry: DropdownEntry) => {
            templatePriorityRequests.push(
              {
                template_name: templatePriorityToUpdate.template_name,
                event_type: customPriority.event_type,
                priority: customPriority.priority,
                product_id: dropdownEntry.product.id
              }
            );
          });
        });
      }
    });

    this._saveTemplatePriorities(templatePriorityRequests);
  }

  private _saveTemplatePriorities(requests: Array<SaveTemplatePriorityRequest>) {
    // Divide requests into chunks of chunkSize
    const chunkSize: number = 500;
    const chunks: SaveTemplatePriorityRequest[][] = [];
    for (let i = 0; i < requests.length; i += chunkSize) {
      chunks.push(requests.slice(i, i + chunkSize));
    }
    // for each chunk, save the template priorities
    const forkJoinUpdates: Array<any> = [];
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      forkJoinUpdates.push(
        this.templatePriorityService.saveTemplatePriorities(chunk, this.templatePriorityService.loggedTemplatePriorities())
        .pipe(
          catchError((err: any) => {
            this.error = err?.error?.message;
            return of(err);
          })
        )
      );
    }

    const delayedForkJoinUpdates = from(forkJoinUpdates).pipe(
      concatMap((update, index) => timer(50).pipe(concatMap(() => update))), // 100ms delay between each request
      toArray()
    );
    forkJoin(delayedForkJoinUpdates)
    .subscribe((saveTemplatePrioritiesResponses) => {
      this.templatePriorityService.setIsLoadingActive(false);
      this.templatePriorityService.setAction('SAVE_OK');
      this.templatePriorityService.setIsSaveEnabled(false);
      this.templatePriorityService.loggedTemplatePriorities.set([]);
      if (saveTemplatePrioritiesResponses.find((response: any) => response !== 'OK')) {
        this.error = 'An error occurred! Some priorities could not be saved.';
        this.templatePriorityService.setError(this.error);
      }
    });
  }

  handleSaveModalOutcome(event) {
    if (event.status && this.dropdownSelectedEntries) {
      if (this.dropdownSelectedEntries.length == 0 || (this.dropdownSelectedEntries.length == 1 && this.dropdownSelectedEntries[0].product.id == null)) {
        // DEFAULT
        this._saveDefaultTemplatePriorities();
      } else if (this.dropdownSelectedEntries.length == 0 || (this.dropdownSelectedEntries.length == 1 && this.dropdownSelectedEntries[0].product.id == 'ALL')) {
        // ALL
        this._saveCustomTemplatePriorities(true);
      } else {
        // FILTERED
        this._saveCustomTemplatePriorities(false);
      }
    }
    this.openSaveModal = false;
  }

  // Export
  handleExportModalOutcome(proceed: {status: boolean, value: string}) {
    logger.info('Handled export-modal outcome: ' + proceed);
    this.exportService.setOpenExportModal(false);
    if (proceed.status) {
      this.exportService.setIsLoadingActive(true);
      const exportRequest: TemplatePriorityExportRequest = {
        export_type: null,
        opp_list: ''
      }
      if (this.dropdownSelectedEntries.length === 1 && this.dropdownSelectedEntries[0]?.product?.id == 'ALL') {
        // Export ALL
        exportRequest.export_type = 'ALL';
        exportRequest.opp_list = this.opps.map((product: Product) => {
          return {
            product_id: product.id,
            product_display_label: product.displayLabel,
            program_id: product.program?.id,
            program_display_label: product.program?.displayLabel,
            operator_id: product.program?.operator?.id,
            operator_display_label: product.program?.operator?.displayLabel
          } as TemplatePriorityExportOppItem
        });
      } else if (this.dropdownSelectedEntries.length === 1 && this.dropdownSelectedEntries[0]?.product?.id == null) {
        // Export DEFAULT
        exportRequest.export_type = 'DEFAULT';
      } else {
        // Export Filtered
        exportRequest.export_type = 'FILTERED';
        exportRequest.opp_list = this.dropdownSelectedEntries.map((entry: DropdownEntry) => {
          return {
            product_id: entry.product.id,
            product_display_label: entry.product.displayLabel,
            program_id: entry.product.program?.id,
            program_display_label: entry.product.program?.displayLabel,
            operator_id: entry.product.program?.operator?.id,
            operator_display_label: entry.product.program?.operator?.displayLabel
          } as TemplatePriorityExportOppItem
        });
      }
      const exportRequestChunks = this._createExportRequestChunks(exportRequest);
      this._joinCsv(exportRequest.export_type, exportRequestChunks);
    }
  }

  private _createExportRequestChunks(request: TemplatePriorityExportRequest): Array<TemplatePriorityExportRequest> {
    const oppList = request.opp_list;
    const chunkSize = 150;
    const chunks: Array<TemplatePriorityExportRequest> = [];
    for (let i = 0; i < oppList.length; i += chunkSize) {
      const chunk = oppList.slice(i, i + chunkSize);
      chunks.push({ export_type: request.export_type, opp_list: btoa(encodeURIComponent(JSON.stringify(chunk))) });
    }
    if (chunks.length === 0) {
      chunks.push(request);
    }
    return chunks
  }

  private _joinCsv(exportType: string, requests: Array<any>) {
    from(requests).pipe(
      // Use concatMap to introduce a delay between each request
      concatMap(request =>
        this.exportService.exportTemplatePriority(request).pipe(
          // Delay the next request
          delayWhen(() => timer(500)),
          catchError((err: any) => {
            this.error = err?.error?.message;
            return of(err);
          }),
        )
      ),
      // Collect all responses into an array
      toArray()
    )
      .subscribe(results => {
        // 'results' contains the responses of all requests, processed sequentially with a delay
        let sheet1Header = '';
        let sheet1Body = '';
        let sheet2Header = '';
        let sheet2Body = '';
        let sheet2HeaderKeys = [];
        const sheet2Rows = [];
        results.filter(result => result.code === 200).forEach((response: any) => {
          if (response.sheets) {
            // Build Sheet 1
            if (response.sheets.length > 0) {
              if (!sheet1Header) {
                sheet1Header = response.sheets[0].header;
              }
              sheet1Body += !sheet1Body ? response.sheets[0].body : '\n' + response.sheets[0].body;
            }
            // Build Sheet 2
            if (response.sheets.length > 1) {
              const sheet2HeaderKeysTemp = response.sheets[1].header.split(',');
              sheet2HeaderKeys = sheet2HeaderKeys.concat(sheet2HeaderKeysTemp.filter(key => !sheet2HeaderKeys.includes(key)));
              const sheet2BodyRows = response.sheets[1].body.split('\n');
              sheet2BodyRows.forEach((row: string) => {
                const rowValues = row.split(',');
                const rowObject = {};
                sheet2HeaderKeysTemp.forEach((key: string, index: number) => {
                  rowObject[key] = rowValues[index];
                });
                sheet2Rows.push(rowObject);
              });
            }
          }
        });
        // Download Sheet 1
        const isoDate: string = new Date().toISOString();
        if (sheet1Header && sheet1Body) {
          this._downloadCsv(sheet1Header + '\n' + sheet1Body, `template-priority-${exportType}-${isoDate}.csv`);
        }
        // Download Sheet 2
        if (sheet2HeaderKeys.length > 0 && sheet2Rows.length > 0) {
          sheet2Header = sheet2HeaderKeys.join(',');
          sheet2Rows.forEach((row: any) => {
            if (sheet2Body) {
              sheet2Body += '\n';
            }
            sheet2HeaderKeys.forEach((key: string, index: number) => {
              const value = (row[key] || '');
              sheet2Body += index === 0 ? value : ',' + value
            });
          });
          this._downloadCsv(sheet2Header + '\n' + sheet2Body, `template-priority-by-event-type-${exportType}-${isoDate}.csv`);
        }
        this.exportService.setIsLoadingActive(false);
      });
  }

  private _downloadCsv(csvData: string, csvName: string) {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', csvName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

}
